/**
 * Whether what the reader is looking at runs past the box that is showing it, in the
 * direction a key is about to move focus.
 *
 * The case this exists for is a card taller than the cell holding it. The reader is on the
 * card, the bottom of it is below the cell's fold, and there is no way to read the rest:
 * the card is not a scroll container, so `canScrollFurther` says no, and the board takes
 * the key and moves to the next card.
 *
 * **What is measured is the roving item, not whatever inside it happens to hold focus.** A
 * caller draws what it likes in a card, and focus reaching a short link near the top of a
 * tall one would otherwise report that everything is visible while most of the card is
 * still below the fold. The reader is reading the card, not the link.
 *
 * **And not the list around it either.** Measuring every ancestor up to the scroller would
 * find the list of a windowed cell's cards, which is taller than the cell by design and so
 * always clipped, and the board would stop moving focus in any cell long enough to scroll.
 * The roving item is the boundary because it is the thing focus is actually on.
 *
 * The scroller also has to have somewhere left to go, so a card clipped by a box that
 * cannot scroll any further never takes a key and leaves it doing nothing.
 *
 * Only for the axis a reader reads along. A jump between swimlanes is a deliberate move
 * past whatever is on screen, so the modifier is the way out of a card too tall to leave.
 *
 * `null` targets and elements the board is not showing answer `false`, which leaves the
 * board to move as it did before.
 */
export function isTargetClipped(target: EventTarget | null, towards: 'start' | 'end'): boolean {
  if (!(target instanceof HTMLElement)) return false

  const container = nearestScroller(target)
  if (container === null) return false

  // Both are fractional on a zoomed page or under a device pixel ratio that is not a whole
  // number, so the ends are never exactly equal.
  const SUB_PIXEL = 1

  // Somewhere left to go the way the key points. Without this, content the scroller cannot
  // reach -- something translated, or pulled out with a negative margin -- would take the
  // key for a scroll that never happens.
  const canReachMore =
    towards === 'start'
      ? container.scrollTop > SUB_PIXEL
      : container.scrollTop + container.clientHeight < container.scrollHeight - SUB_PIXEL
  if (!canReachMore) return false

  const item = rovingItem(target, container)
  const box = item.getBoundingClientRect()
  const viewport = container.getBoundingClientRect()

  return towards === 'start' ? box.top < viewport.top - SUB_PIXEL : box.bottom > viewport.bottom + SUB_PIXEL
}

/**
 * The element the board's roving tabindex is on, which is the nearest ancestor carrying a
 * `tabindex` and, when focus is on that element itself, the element itself.
 *
 * Read from the attribute rather than from the board's own state, because this is asked
 * inside the key handler, which has an event target and not a component. Every roving item
 * the board draws carries one: a card is `-1` at rest, a cell body, a swimlane's name and
 * each paging ghost the same. A caller that puts a `tabindex` on something of its own
 * inside a card makes that the boundary, which is the right answer anyway -- it is its own
 * stop, so it is what focus is on.
 */
function rovingItem(target: HTMLElement, container: HTMLElement): HTMLElement {
  const found = target.closest<HTMLElement>('[tabindex]')

  return found !== null && found !== container && container.contains(found) ? found : target
}

/**
 * The nearest ancestor that scrolls downwards, which for a card is the cell it is in.
 *
 * Overflowing is not the same as scrolling: an element whose overflow is `visible` or
 * `hidden` reports a scroll size larger than its client size and the browser will not
 * scroll it for a key, so it is not what is clipping anything a reader can recover.
 */
function nearestScroller(element: HTMLElement): HTMLElement | null {
  const view = element.ownerDocument.defaultView
  if (view === null) return null

  for (let parent = element.parentElement; parent !== null; parent = parent.parentElement) {
    const {overflowY} = view.getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll') return parent
  }

  return null
}
